import React from 'react'
import { Select } from '@/components/ui'
import { SCREENSHOT_MODE_OPTIONS } from '@/features/organization/constants/settingsOptions'
import { cn } from '@/lib/cn'
import {
  FieldGrid,
  FieldGroup,
  LabeledToggleRow,
  NumberField,
  SettingsPane,
  ToggleList,
} from '../organization-ui'
import type { OrganizationSettingsFormProps } from '../settings/organizationSettingsForm.types'

export const AgentPane: React.FC<OrganizationSettingsFormProps> = ({
  register,
  setValue,
  watch,
  errors,
  disabled = false,
}) => {
  const screenshotEnabled = watch('agentSettings.screenshotEnabled') ?? false
  const inactivityAlertEnabled = watch('agentSettings.inactivityAlertEnabled') ?? false

  return (
    <SettingsPane
      title="Desktop agent"
      description="What the desktop agent records on staff machines, and when it raises an alert."
    >
      <FieldGroup label="Screenshots">
        <ToggleList>
          <LabeledToggleRow
            label="Capture screenshots"
            description="Allow the desktop agent to capture screenshots during tracked shifts."
            checked={screenshotEnabled}
            disabled={disabled}
            onChange={(checked) =>
              setValue('agentSettings.screenshotEnabled', checked, { shouldDirty: true })
            }
          />
        </ToggleList>
        <div
          className={cn(
            'mt-4 transition-opacity duration-200',
            !screenshotEnabled && 'opacity-55'
          )}
        >
          <FieldGrid columns={2}>
            <NumberField
              label="Capture interval"
              min={0}
              unit="min"
              disabled={disabled || !screenshotEnabled}
              hint={screenshotEnabled ? undefined : 'Enable capture to configure.'}
              error={errors.agentSettings?.screenshotIntervalMinutes?.message}
              {...register('agentSettings.screenshotIntervalMinutes', { valueAsNumber: true })}
            />
            <Select
              label="Capture mode"
              options={SCREENSHOT_MODE_OPTIONS}
              value={watch('agentSettings.screenshotMode') ?? ''}
              disabled={disabled || !screenshotEnabled}
              onChange={(v) =>
                setValue('agentSettings.screenshotMode', v as 'random' | 'fixed', {
                  shouldDirty: true,
                })
              }
              placeholder="Select mode"
            />
          </FieldGrid>
        </div>
      </FieldGroup>

      <FieldGroup label="Inactivity">
        <ToggleList>
          <LabeledToggleRow
            label="Alert on inactivity"
            description="Notify admins when a tracked session goes idle past the threshold."
            checked={inactivityAlertEnabled}
            disabled={disabled}
            onChange={(checked) =>
              setValue('agentSettings.inactivityAlertEnabled', checked, { shouldDirty: true })
            }
          />
        </ToggleList>
        <div
          className={cn(
            'mt-4 transition-opacity duration-200',
            !inactivityAlertEnabled && 'opacity-55'
          )}
        >
          <FieldGrid columns={2}>
            <NumberField
              label="Inactivity threshold"
              min={0}
              unit="min"
              disabled={disabled || !inactivityAlertEnabled}
              hint={inactivityAlertEnabled ? undefined : 'Enable alerts to configure.'}
              error={errors.agentSettings?.inactivityThresholdMinutes?.message}
              {...register('agentSettings.inactivityThresholdMinutes', { valueAsNumber: true })}
            />
          </FieldGrid>
        </div>
      </FieldGroup>
    </SettingsPane>
  )
}

AgentPane.displayName = 'AgentPane'
